import React, { useState } from "react";
import {
  TextField,
  IconButton,
  Popover,
  Box,
  Typography,
  InputAdornment,
} from "@mui/material";
import { AccessTime as AccessTimeIcon } from "@mui/icons-material";

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

const cellSx = (selected) => ({
  width: 36,
  height: 32,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 1,
  cursor: "pointer",
  fontSize: "13px",
  fontWeight: selected ? 700 : 400,
  color: selected ? "#fff" : "#333",
  backgroundColor: selected ? "#ff6a00" : "transparent",
  "&:hover": {
    backgroundColor: selected ? "#e55d00" : "#f0f0f0",
  },
  userSelect: "none",
});

function TimePickerInput({ value, onChange, sx }) {
  const [anchorEl, setAnchorEl] = useState(null);

  // 現在の値から時・分を取得
  const parts = (value || "").split(":");
  const currentHour = parts[0] ? parseInt(parts[0], 10) : null;
  const currentMinute = parts[1] ? parseInt(parts[1], 10) : null;

  const handleOpen = (e) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const buildTime = (h, m) => {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return `${hh}:${mm}`;
  };

  const handleHourClick = (h) => {
    const m = currentMinute != null && !isNaN(currentMinute) ? currentMinute : 0;
    onChange(buildTime(h, m));
  };

  const handleMinuteClick = (m) => {
    const h = currentHour != null && !isNaN(currentHour) ? currentHour : 0;
    onChange(buildTime(h, m));
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <TextField
        label="時間"
        placeholder="hh:mm"
        size="small"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        sx={{ width: 120, ...sx }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleOpen} edge="end">
                <AccessTimeIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ display: "flex", gap: 0, p: 1.5, maxHeight: 320 }}>
          {/* 時 */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography
              sx={{ fontSize: "11px", fontWeight: 700, color: "#888", mb: 0.5 }}
            >
              時
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(4, 1fr)",
                gap: "2px",
                overflowY: "auto",
                maxHeight: 260,
              }}
            >
              {HOURS.map((h) => (
                <Box
                  key={h}
                  onClick={() => handleHourClick(h)}
                  sx={cellSx(currentHour === h)}
                >
                  {h}
                </Box>
              ))}
            </Box>
          </Box>

          {/* 区切り */}
          <Box
            sx={{
              width: "1px",
              bgcolor: "#e0e0e0",
              mx: 1,
              alignSelf: "stretch",
            }}
          />

          {/* 分 */}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography
              sx={{ fontSize: "11px", fontWeight: 700, color: "#888", mb: 0.5 }}
            >
              分
            </Typography>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "2px",
              }}
            >
              {MINUTES.map((m) => (
                <Box
                  key={m}
                  onClick={() => handleMinuteClick(m)}
                  sx={cellSx(currentMinute === m)}
                >
                  {String(m).padStart(2, "0")}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Popover>
    </>
  );
}

export default TimePickerInput;
